/**
 * Phone number normalization utilities
 * Converts phone numbers to E.164 format for consistent storage
 */

import { parsePhoneNumber, CountryCode } from 'libphonenumber-js';

/**
 * Normalize phone to E.164 format (+1234567890)
 * Returns null if phone cannot be confidently normalized
 *
 * MVP: US numbers only. International support deferred.
 * No silent garbage values - if invalid, return null.
 */
export function normalizePhone(phone: string | null | undefined, defaultCountry: CountryCode = 'US'): string | null {
  if (!phone) return null;

  try {
    const parsed = parsePhoneNumber(phone, defaultCountry);
    if (parsed && parsed.isValid()) {
      return parsed.number; // E.164 format
    }
  } catch (error) {
    // Parsing failed, try manual normalization
  }

  // Manual normalization for US numbers only
  const digits = phone.replace(/\D/g, '');

  // If it's 10 digits, assume US and add +1
  if (digits.length === 10) {
    return `+1${digits}`;
  }

  // If it's 11 digits starting with 1, add +
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  // Cannot confidently normalize - return null
  // Do not allow garbage values to flow through
  return null;
}

/**
 * Format E.164 phone for display
 * +12345678900 → (234) 567-8900
 */
export function formatPhoneDisplay(phone: string): string {
  if (!phone) return '';

  try {
    const parsed = parsePhoneNumber(phone);
    if (parsed) {
      return parsed.formatNational(); // e.g., (234) 567-8900
    }
  } catch (error) {
    // Fallback to raw display
  }

  return phone;
}
